import { CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSubscription } from '../queries';

/**
 * The dashboard's billing nudge — and it renders in exactly two situations:
 * a running trial and a failed payment.
 *
 * A healthy subscription shows NOTHING. A card that says "active, all good" on
 * every login is a permanent advertisement for a page nobody needs to visit,
 * and this screen belongs to teaching, not to the invoice.
 */
export function SubscriptionStatusCard() {
  const { t } = useTranslation(['billing']);
  const subscription = useSubscription();
  const data = subscription.data;

  if (!data?.billing_enabled) return null;
  if (data.status !== 'trialing' && data.status !== 'past_due') return null;

  const trialing = data.status === 'trialing';
  const daysLeft = data.trial_days_left;

  let body: string;
  if (!trialing) {
    body = t('billing:dashboard.pastDueBody');
  } else if (daysLeft === null) {
    body = t('billing:summary.trialSoon');
  } else if (daysLeft === 0) {
    body = t('billing:dashboard.trialBodyToday');
  } else {
    body = t('billing:dashboard.trialBody', { count: daysLeft });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CreditCard className="text-muted-foreground size-4" aria-hidden />
          {trialing ? t('billing:dashboard.trialTitle') : t('billing:dashboard.pastDueTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">{body}</p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/billing">{t('billing:dashboard.open')}</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
